import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
} from "@tanstack/react-query";
import {
  getOvertimeSummaryList,
  getRoundScoreOrderList,
  getScoreCalendarList,
  getScoreCardView,
  getScoreOrderList,
  getUserScoreCards,
  getTournamentCalendarList,
  getTournamentCalendarsList,
  getTournamentDetails,
  getTournamentQrList,
  getTournamentsList,
  getTournamentTerms,
  postTournamentQrCode,
} from "../v1/tournament.service";
import type { TournamentListParams } from "../dto/response/torunament.response";
import type {
  TournamentQrParams,
} from "../dto/request/tournament.request";

const TOURNAMENT_LIST_PAGE_SIZE = 20;
// 확정 전 실시간 순위 갱신 주기
const LIVE_RANKING_REFETCH_MS = 30 * 1000;

// 대회 목록 무한 스크롤 (서버 페이징 + 필터)
export const useTournamentListInfinite = (
  filters: Omit<TournamentListParams, "page" | "pageSize"> = {},
) =>
  infiniteQueryOptions({
    queryKey: ["tournament-list", filters],
    queryFn: ({ pageParam }) =>
      getTournamentsList({
        ...filters,
        page: pageParam,
        pageSize: TOURNAMENT_LIST_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const paging = lastPage.data?.paging;
      if (!paging) return undefined;
      return paging.page < paging.totalPages ? paging.page + 1 : undefined;
    },
    retry: 1,
  });

export const useTournamentDetail = (id: string | number) =>
  queryOptions({
    queryKey: ["tournament-detail", id],
    queryFn: () => getTournamentDetails(id),
    retry: 1,
  });

export const useTournamentCalendarsList = (id: string | number, date: string) =>
  queryOptions({
    queryKey: ["tournament-calendars-list", id, date],
    queryFn: () => getTournamentCalendarsList(id, date),
    retry: 1,
  });

export const useTournamentCalendarList = (id: string | number) =>
  queryOptions({
    queryKey: ["tournament-calendar-list", id],
    queryFn: () => getTournamentCalendarList(id),
    enabled: !!id,
    retry: 1,
  });

export const useScoreCalendarList = (id: string | number) =>
  queryOptions({
    queryKey: ["score-calendar-list", id],
    queryFn: () => getScoreCalendarList(id),
    enabled: !!id,
    retry: 1,
  });

export const useScoreOrderList = (
  id: string | number,
  calendarId: string | number | null | undefined,
) =>
  queryOptions({
    queryKey: ["score-order-list", id, calendarId],
    queryFn: () => getScoreOrderList(id, calendarId as string | number),
    enabled: !!id && calendarId != null && calendarId !== "",
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.data?.isRankingFinal ? false : LIVE_RANKING_REFETCH_MS,
  });

export const useRoundScoreOrderList = (
  id: string | number,
  roundId: string | number | null | undefined,
  gender?: 0 | 1 | null,
) =>
  queryOptions({
    queryKey: ["round-score-order-list", id, roundId, gender],
    queryFn: () => getRoundScoreOrderList(id, roundId, gender ?? undefined),
    enabled: !!id,
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.data?.isRankingFinal ? false : LIVE_RANKING_REFETCH_MS,
  });

export const useScoreCardView = (
  id: string | number,
  calendarId: string | number | null | undefined,
  scoreCardId: string | number | null | undefined,
) =>
  queryOptions({
    queryKey: ["score-card-view", id, calendarId, scoreCardId],
    queryFn: () =>
      getScoreCardView(
        id,
        calendarId as string | number,
        scoreCardId as string | number,
      ),
    enabled: !!id && calendarId != null && scoreCardId != null,
    retry: 1,
  });

export const useUserScoreCards = (
  id: string | number,
  calendarId: string | number | null | undefined,
  userId: string | number | null | undefined,
  roundId?: string | number | null,
) =>
  queryOptions({
    queryKey: ["user-score-cards", id, calendarId, userId, roundId],
    queryFn: () =>
      getUserScoreCards(
        id,
        calendarId as string | number,
        userId as string | number,
        roundId,
      ),
    enabled: !!id && calendarId != null && userId != null,
    retry: 1,
  });

export const useTournamentTerms = (id: string | number) =>
  queryOptions({
    queryKey: ["tournament-terms", id],
    queryFn: () => getTournamentTerms(id),
    retry: 1,
  });

export const useTournamentQrList = (
  id: string | number,
  params: TournamentQrParams | null,
) =>
  queryOptions({
    queryKey: ["tournament-qr-list", id, params],
    queryFn: () => getTournamentQrList(id, params as TournamentQrParams),
    enabled: !!id && params != null,
    retry: 1,
  });

export const useTournamentQrCode = (
  id: string | number,
  params: TournamentQrParams | null,
) =>
  useMutation({
    mutationFn: (qrId: number) => {
      if (!params) throw new Error("QR query parameters are unavailable.");
      return postTournamentQrCode(id, { ...params, qrId });
    },
  });

// 연장전 스코어 리스트
export const useOvertimeSummaryList = (
  tournamentId: string | number | null | undefined,
  roundId: string | number,
) =>
  queryOptions({
    queryKey: ["overtime-summary-list", tournamentId, roundId],
    queryFn: () => getOvertimeSummaryList(tournamentId as string, roundId),
    enabled: tournamentId != null,
    retry: 1,
  });
